import { useMemo, useState, useRef, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Users, UserX, MapPin, BarChart3, Sparkles, AlertCircle, Euro, Package, Play } from 'lucide-react'
import {
  Card, PageHeader, Badge, Button,
  Tabs, TabsList, TabsTrigger, TabsContent,
  LoadingState, ErrorState, EmptyState, SimpleTable,
  Modal, Select, FilterChip,
} from '@/components/ui'
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
} from 'recharts'
import api from '@/lib/api.ts'
import { useTenantStore } from '@/stores/tenants'

// Server-Fehler aus dem axios-Error extrahieren. Backend antwortet bei
// 500 mit `{ error, stack }` (siehe middleware/errorHandler.ts) — diese
// Felder gehoeren in die aufklappbaren „Technischen Details", damit der
// User die echte Ursache sieht (CLAUDE.md: keine Server-Logs fuer User).
function extractErrorDetails(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: string; stack?: string; details?: unknown } }; message?: string }
  const status = e?.response?.status
  const body = e?.response?.data
  const lines: string[] = []
  if (status) lines.push(`HTTP ${status}`)
  if (body?.error) lines.push(body.error)
  if (body?.details) lines.push(typeof body.details === 'string' ? body.details : JSON.stringify(body.details, null, 2))
  if (body?.stack) lines.push(body.stack)
  if (lines.length === 0) lines.push(e?.message ?? String(err))
  return lines.join('\n\n')
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface IndustryBucket {
  sapCode: string | null
  industryName: string
  count: number
  percentage: number
}
interface IndustryResponse {
  total: number
  buckets: IndustryBucket[]
  unknown: number
  activity: 'all' | 'active' | 'inactive'
  thresholdDays: number
  activityDataAvailable: boolean
}
interface GeoBucket { key: string; label: string; count: number }
interface GeoResponse {
  byCountry: GeoBucket[]
  byState: GeoBucket[]
  byZipPrefix: GeoBucket[]
  totals?: {
    total: number
    withZip: number
    withCountry: number
    sourceStored: number
    sourceRaw: number
    sourceMerged: number
    sourceNone: number
  }
  rawSample?: {
    cardCode: string | null
    addressKeys: string[]
    hasBPAddresses: boolean
    bpAddressesLength: number
  } | null
}
interface ActivityResponse {
  total: number
  active: number
  inactive: number
  newLast90Days: number
  newPerQuarter: { quarter: string; count: number }[]
  activityDefinition?: string
}
interface SummaryResponse {
  total: number
  active: number
  inactive: number
  newLast90Days: number
  withoutIndustry: number
}
interface Suggestion {
  id: string
  customerId: string
  customerName: string
  suggestedSapCode: string
  suggestedName: string
  confidence: number | null
  reasoning: string | null
  modelUsed: string | null
  createdAt: string
}
interface CatalogEntry { sapCode: string; name: string }
interface EnrichmentStatus {
  catalogCount: number
  pendingCount: number
  autoAppliedCount: number
  errorCount: number
  customersWithoutIndustry: number
  customersWithLocalIndustry: number
  lastSyncErrors: Array<{ customerId: string; customerName: string; error: string; createdAt: string }>
}

// ─── Page ───────────────────────────────────────────────────────────────────

const PIE_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#ea580c', '#84cc16', '#db2777', '#475569']

export function AuswertungenKundenPage() {
  const [tab, setTab] = useState('industries')
  const [geoTab, setGeoTab] = useState<'country' | 'state' | 'zip'>('zip')
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)

  const summary = useQuery<SummaryResponse>({
    queryKey: ['customer-analytics', 'summary', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/summary').then((r) => r.data),
  })

  // Wirklich offene KI-Vorschlaege fuer das Tab-Badge — dedizierter Count-Endpoint
  // statt limit=1+length, damit der Badge die echte Anzahl zeigt.
  const pendingCount = useQuery<{ count: number }>({
    queryKey: ['customer-analytics', 'suggestions-count', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/industry-suggestions-count').then((r) => r.data),
  })

  // Branchen-Katalog-Status auf Page-Ebene: beide Tabs (Industries + Suggestions)
  // brauchen die Info, ob die industries-Tabelle leer ist, um einen Hinweis anzuzeigen.
  const catalogStatus = useQuery<CatalogEntry[]>({
    queryKey: ['customer-analytics', 'industry-catalog', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/industry-catalog').then((r) => r.data),
  })
  const catalogIsEmpty = !catalogStatus.isLoading && (catalogStatus.data?.length ?? 0) === 0

  const [generatorOpen, setGeneratorOpen] = useState(false)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Auswertung: Kunden"
        description="Branchenverteilung, Geografie, Aktivität sowie KI-Branchen-Vorschläge."
        actions={
          <Button
            variant="primary"
            onClick={() => setGeneratorOpen(true)}
          >
            <Sparkles className="w-4 h-4" /> Branchen-Katalog generieren
          </Button>
        }
      />

      <CatalogGeneratorModal open={generatorOpen} onClose={() => setGeneratorOpen(false)} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Kunden gesamt" value={summary.data?.total} icon={<Users className="w-5 h-5 text-brand-600" />} loading={summary.isLoading} />
        <Kpi label="Aktiv" value={summary.data?.active} icon={<Users className="w-5 h-5 text-green-600" />} loading={summary.isLoading} />
        <Kpi label="Inaktiv" value={summary.data?.inactive} icon={<UserX className="w-5 h-5 text-gray-400" />} loading={summary.isLoading} />
        <Kpi
          label="Ohne Branche"
          value={summary.data?.withoutIndustry}
          icon={<AlertCircle className="w-5 h-5 text-orange-500" />}
          loading={summary.isLoading}
          onClick={() => setTab('suggestions')}
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="industries"><Building2 className="w-4 h-4" /> Branchen</TabsTrigger>
          <TabsTrigger value="sales"><Euro className="w-4 h-4" /> Umsatz</TabsTrigger>
          <TabsTrigger value="geography"><MapPin className="w-4 h-4" /> Geografie</TabsTrigger>
          <TabsTrigger value="activity"><BarChart3 className="w-4 h-4" /> Aktivität</TabsTrigger>
          <TabsTrigger value="suggestions">
            <Sparkles className="w-4 h-4" /> KI-Vorschläge
            {pendingCount.data?.count ? (
              <Badge variant="warning" size="sm">{pendingCount.data.count}</Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="industries">
          <IndustriesTab catalogIsEmpty={catalogIsEmpty} />
        </TabsContent>

        <TabsContent value="sales">
          <SalesTab />
        </TabsContent>

        <TabsContent value="geography">
          <GeographyTab geoTab={geoTab} setGeoTab={setGeoTab} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab />
        </TabsContent>

        <TabsContent value="suggestions">
          <SuggestionsTab catalogIsEmpty={catalogIsEmpty} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ─── KPI ────────────────────────────────────────────────────────────────────

function Kpi({ label, value, icon, loading, onClick }: {
  label: string
  value: number | undefined
  icon: ReactNode
  loading?: boolean
  onClick?: () => void
}) {
  const inner = (
    <Card.Body className="p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-small text-gray-500">{label}</p>
      </div>
      <div className="flex items-end gap-2">
        <div className="p-2 rounded-md bg-gray-50">{icon}</div>
        <p className="text-h1 text-gray-900">{loading ? '…' : value ?? '—'}</p>
      </div>
    </Card.Body>
  )
  if (onClick) {
    return (
      <Card className="cursor-pointer hover:shadow-md transition" onClick={onClick}>
        {inner}
      </Card>
    )
  }
  return <Card>{inner}</Card>
}

// ─── Branchen-Tab ───────────────────────────────────────────────────────────

type ActivityFilter = 'all' | 'active' | 'inactive'

function IndustriesTab({ catalogIsEmpty }: { catalogIsEmpty?: boolean }) {
  const [activity, setActivity] = useState<ActivityFilter>('all')
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const q = useQuery<IndustryResponse>({
    queryKey: ['customer-analytics', 'industries', activeTenantIds, activity],
    queryFn: () => api.get(`/customer-analytics/industries?activity=${activity}`).then((r) => r.data),
  })

  const filterBar = (
    <div className="flex items-center gap-2 flex-wrap">
      <FilterChip active={activity === 'all'} onClick={() => setActivity('all')}>Alle</FilterChip>
      <FilterChip active={activity === 'active'} onClick={() => setActivity('active')}>Nur aktiv</FilterChip>
      <FilterChip active={activity === 'inactive'} onClick={() => setActivity('inactive')}>Nur inaktiv</FilterChip>
      {q.data ? (
        <span className="ml-auto text-small text-gray-500">
          {q.data.activityDataAvailable
            ? `Aktiv-Schwelle: letzte Rechnung ≤ ${q.data.thresholdDays} Tage`
            : 'Aktiv-Schwelle: SAP-Flag is_active (kein Rechnungs-Cache)'}
        </span>
      ) : null}
    </div>
  )

  const emptyCatalogBanner = catalogIsEmpty ? (
    <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-small text-amber-800">
      <div className="font-medium">Branchen-Katalog ist leer — KI-Erkennung deaktiviert</div>
      <div className="mt-1">
        Die <code>industries</code>-Tabelle enthält noch keine Einträge. Klicke oben rechts auf{' '}
        <strong>„Branchen-Katalog generieren"</strong>, um per KI passende NACE-Branchen vorzuschlagen
        und anschließend in SAP zu übernehmen. Danach werden Kunden ohne Branche beim nächsten
        Sync automatisch analysiert.
      </div>
    </div>
  ) : null

  if (q.isLoading) return <div className="space-y-3">{filterBar}{emptyCatalogBanner}<LoadingState label="Lade Branchenverteilung…" /></div>
  if (q.isError) return <div className="space-y-3">{filterBar}{emptyCatalogBanner}<ErrorState title="Branchenverteilung konnte nicht geladen werden" details={extractErrorDetails(q.error)} /></div>
  if (!q.data || q.data.total === 0) {
    return <div className="space-y-3">{filterBar}{emptyCatalogBanner}<EmptyState title="Keine Kunden im gewählten Filter" description="Wähle einen anderen Aktivitäts-Filter oder synchronisiere zuerst Kunden + Rechnungen aus SAP." /></div>
  }

  // Top 10 anzeigen, Rest in „Sonstige" zusammenfassen, damit der Donut lesbar bleibt.
  const sorted = [...q.data.buckets].sort((a, b) => b.count - a.count)
  const top = sorted.slice(0, 10)
  const restCount = sorted.slice(10).reduce((s, b) => s + b.count, 0)
  const chartData = restCount > 0
    ? [...top, { industryName: 'Sonstige', count: restCount, sapCode: null, percentage: (restCount / q.data.total) * 100 }]
    : top

  return (
    <div className="space-y-3">
      {filterBar}
      {emptyCatalogBanner}
      <Card>
      <Card.Header>
        <Card.Title>Branchenverteilung</Card.Title>
        <span className="text-small text-gray-500">{q.data.total} Kunden, {q.data.buckets.length} Branchen</span>
      </Card.Header>
      <Card.Body>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="industryName"
                  cx="50%" cy="50%"
                  innerRadius={60} outerRadius={100}
                  paddingAngle={2}
                >
                  {chartData.map((_entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip formatter={(v: number, _n, p) => [`${v} Kunden (${((v / q.data!.total) * 100).toFixed(1)}%)`, p?.payload?.industryName]} />
                {/* Legend bewusst weggelassen — bei 15+ Branchen ueberlagert sie den Chart.
                    Die Tabelle rechts hat alle Branchen mit Anzahl + Anteil. */}
              </PieChart>
            </ResponsiveContainer>
          </div>
          <BucketTable
            headers={['Branche', 'Anzahl', 'Anteil']}
            rows={sorted.map((b) => ({
              key: b.sapCode ?? b.industryName,
              cells: [b.industryName, String(b.count), `${b.percentage.toFixed(1)}%`],
              alignRight: [false, true, true],
            }))}
          />
        </div>
      </Card.Body>
    </Card>
    </div>
  )
}

// ─── Umsatz-Tab ─────────────────────────────────────────────────────────────

interface ProductRevenueRow {
  itemCode: string
  itemDescription: string | null
  totalRevenue: number
  invoiceCount: number
  customerCount: number
}
interface IndustryRevenueRow {
  industryName: string
  totalRevenue: number
  customerCount: number
  invoiceCount: number
}
interface SalesSummary {
  totalInvoices: number
  totalRevenue: number
  lastSyncedAt: string | null
  oldestInvoiceDate: string | null
  newestInvoiceDate: string | null
  backfill: {
    yearsCompleted: number
    yearsRemaining: number
    oldestCompletedYear: number | null
    newestCompletedYear: number | null
    errorYears: number[]
    errorMessages: Array<{ year: number; message: string }>
  } | null
}

const EUR = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

function thisYearRange(): { from: string; to: string } {
  const today = new Date()
  const from = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10)
  const to = today.toISOString().slice(0, 10)
  return { from, to }
}

type SalesPreset = 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'lastQuarter' | 'thisYear' | 'lastYear'

function SalesTab() {
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const initial = thisYearRange()
  const [from, setFrom] = useState(initial.from)
  const [to, setTo] = useState(initial.to)
  const [applied, setApplied] = useState(initial)
  const [activePreset, setActivePreset] = useState<SalesPreset | null>('thisYear')

  const params = `?from=${applied.from}&to=${applied.to}&limit=10`

  const summary = useQuery<SalesSummary>({
    queryKey: ['customer-analytics', 'sales-summary', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/sales-summary').then((r) => r.data),
  })

  const products = useQuery<ProductRevenueRow[]>({
    queryKey: ['customer-analytics', 'top-products', activeTenantIds, applied.from, applied.to],
    queryFn: () => api.get(`/customer-analytics/top-products${params}`).then((r) => r.data),
  })

  const industries = useQuery<IndustryRevenueRow[]>({
    queryKey: ['customer-analytics', 'top-industries-revenue', activeTenantIds, applied.from, applied.to],
    queryFn: () => api.get(`/customer-analytics/top-industries-by-revenue${params}`).then((r) => r.data),
  })

  const applyRange = () => {
    setActivePreset(null)
    setApplied({ from, to })
  }

  // Zeitraum-Presets
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  const applyPreset = (preset: SalesPreset) => {
    const today = new Date()
    let f: Date, t: Date
    switch (preset) {
      case 'thisMonth':
        f = new Date(today.getFullYear(), today.getMonth(), 1)
        t = today
        break
      case 'lastMonth':
        f = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        t = new Date(today.getFullYear(), today.getMonth(), 0)
        break
      case 'thisQuarter': {
        const q = Math.floor(today.getMonth() / 3)
        f = new Date(today.getFullYear(), q * 3, 1)
        t = today
        break
      }
      case 'lastQuarter': {
        const q = Math.floor(today.getMonth() / 3) - 1
        const yr = q < 0 ? today.getFullYear() - 1 : today.getFullYear()
        const lq = (q + 4) % 4
        f = new Date(yr, lq * 3, 1)
        t = new Date(yr, lq * 3 + 3, 0)
        break
      }
      case 'thisYear':
        f = new Date(today.getFullYear(), 0, 1)
        t = today
        break
      case 'lastYear':
        f = new Date(today.getFullYear() - 1, 0, 1)
        t = new Date(today.getFullYear() - 1, 11, 31)
        break
    }
    const nf = fmt(f)
    const nt = fmt(t)
    setFrom(nf)
    setTo(nt)
    setApplied({ from: nf, to: nt })
    setActivePreset(preset)
  }

  const PRESETS: { key: SalesPreset; label: string }[] = [
    { key: 'thisMonth', label: 'Aktueller Monat' },
    { key: 'lastMonth', label: 'Letzter Monat' },
    { key: 'thisQuarter', label: 'Aktuelles Quartal' },
    { key: 'lastQuarter', label: 'Letztes Quartal' },
    { key: 'thisYear', label: 'Aktuelles Jahr' },
    { key: 'lastYear', label: 'Letztes Jahr' },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <Card.Title>Zeitraum & Sync-Status</Card.Title>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map((p) => (
              <FilterChip
                key={p.key}
                active={activePreset === p.key}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </FilterChip>
            ))}
            <input
              type="date"
              className="border border-gray-300 rounded-md px-2 py-1 text-body"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <span className="text-small text-gray-500">–</span>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-2 py-1 text-body"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <Button variant="primary" size="sm" onClick={applyRange}>Übernehmen</Button>
            <div className="ml-auto text-small text-gray-500">
              {summary.data
                ? `${summary.data.totalInvoices} Rechnungen lokal · Stand: ${
                    summary.data.lastSyncedAt
                      ? new Date(summary.data.lastSyncedAt).toLocaleString('de-DE')
                      : '—'
                  }`
                : 'Sync-Status wird geladen…'}
            </div>
          </div>
          {summary.data && summary.data.totalInvoices === 0 ? (
            <div className="mt-3 text-small text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              Noch keine Rechnungen im Dokument-Cache. Settings → Scheduled&nbsp;Jobs → „SAP&nbsp;Dokumente — Cache aktualisieren" („Jetzt ausführen") triggert den Sync.
            </div>
          ) : null}
          {summary.data && summary.data.backfill && summary.data.backfill.yearsRemaining > 0 ? (
            <div className="mt-3 text-small text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <div className="font-medium">
                Historische Daten werden noch nachgeladen — pro Cron-Tick werden weitere Jahre aus SAP gezogen.
              </div>
              <div className="mt-1 text-amber-700">
                Rechnungen im Cache:{' '}
                {summary.data.oldestInvoiceDate && summary.data.newestInvoiceDate
                  ? <>von <strong>{summary.data.oldestInvoiceDate}</strong> bis <strong>{summary.data.newestInvoiceDate}</strong></>
                  : '—'}
                {' · '}
                Backfill abgeschlossen für{' '}
                <strong>{summary.data.backfill.yearsCompleted}</strong> Jahr(e)
                {summary.data.backfill.oldestCompletedYear !== null && summary.data.backfill.newestCompletedYear !== null
                  ? <> ({summary.data.backfill.oldestCompletedYear}–{summary.data.backfill.newestCompletedYear})</>
                  : null}
                {', '}
                <strong>{summary.data.backfill.yearsRemaining}</strong> Jahr(e) ausstehend.
                {summary.data.backfill.errorYears.length > 0 ? (
                  <> Fehler-Jahre: {summary.data.backfill.errorYears.join(', ')}.</>
                ) : null}
              </div>
              {summary.data.backfill.errorMessages && summary.data.backfill.errorMessages.length > 0 ? (
                <details className="mt-2">
                  <summary className="cursor-pointer text-amber-800 font-medium">
                    SAP-Fehler anzeigen ({summary.data.backfill.errorMessages.length})
                  </summary>
                  <ul className="mt-1 space-y-1 text-amber-900 text-caption font-mono">
                    {summary.data.backfill.errorMessages.map((e) => (
                      <li key={e.year}>
                        <strong>{e.year}:</strong> {e.message}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              <div className="mt-1 text-amber-700">
                Für sofortige Vollbefüllung: Settings → Scheduled&nbsp;Jobs → „SAP&nbsp;Dokumente — Cache aktualisieren" mehrfach „Jetzt ausführen".
              </div>
            </div>
          ) : null}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title><Package className="w-4 h-4 inline mr-1" /> Top 10 Produkte nach Umsatz</Card.Title>
        </Card.Header>
        <Card.Body>
          {products.isLoading ? (
            <LoadingState label="Lade Top-Produkte…" />
          ) : products.isError ? (
            <ErrorState title="Top-Produkte konnten nicht geladen werden" details={extractErrorDetails(products.error)} />
          ) : (products.data ?? []).length === 0 ? (
            <EmptyState title="Keine Daten im gewählten Zeitraum" />
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="h-72">
                <ResponsiveContainer>
                  <BarChart data={products.data ?? []} layout="vertical" margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => EUR.format(v)} />
                    <YAxis dataKey="itemCode" type="category" width={120} tick={{ fontSize: 12 }} />
                    <ReTooltip formatter={(v: number) => EUR.format(v)} />
                    <Bar dataKey="totalRevenue" fill="#16a34a" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.Tr>
                    <SimpleTable.Th>Artikel</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Umsatz</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Rechn.</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Kunden</SimpleTable.Th>
                  </SimpleTable.Tr>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {(products.data ?? []).map((p, i) => (
                    <SimpleTable.Tr key={`${p.itemCode}-${i}`}>
                      <SimpleTable.Td>
                        <div className="font-mono text-caption text-gray-500">{p.itemCode}</div>
                        <div className="text-small">{p.itemDescription ?? '—'}</div>
                      </SimpleTable.Td>
                      <SimpleTable.Td className="text-right font-medium">{EUR.format(p.totalRevenue)}</SimpleTable.Td>
                      <SimpleTable.Td className="text-right">{p.invoiceCount}</SimpleTable.Td>
                      <SimpleTable.Td className="text-right">{p.customerCount}</SimpleTable.Td>
                    </SimpleTable.Tr>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title><Building2 className="w-4 h-4 inline mr-1" /> Top 10 Branchen nach Umsatz</Card.Title>
        </Card.Header>
        <Card.Body>
          {industries.isLoading ? (
            <LoadingState label="Lade Top-Branchen…" />
          ) : industries.isError ? (
            <ErrorState title="Top-Branchen konnten nicht geladen werden" details={extractErrorDetails(industries.error)} />
          ) : (industries.data ?? []).length === 0 ? (
            <EmptyState title="Keine Daten im gewählten Zeitraum" />
          ) : (
            <div className="grid lg:grid-cols-2 gap-6">
              <div style={{ height: Math.max(288, (industries.data?.length ?? 0) * 34) }}>
                <ResponsiveContainer>
                  <BarChart data={industries.data ?? []} layout="vertical" margin={{ left: 8, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => EUR.format(v)} />
                    <YAxis
                      dataKey="industryName"
                      type="category"
                      width={60}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(v: string) => {
                        const code = v?.split('—')[0]?.trim()
                        if (code && /^\d/.test(code)) return code
                        return v.length > 10 ? v.slice(0, 10) + '…' : v
                      }}
                    />
                    <ReTooltip formatter={(v: number, _n, p) => [EUR.format(v), p?.payload?.industryName]} />
                    <Bar dataKey="totalRevenue" fill="#2563eb" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.Tr>
                    <SimpleTable.Th>Branche</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Umsatz</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Kunden</SimpleTable.Th>
                    <SimpleTable.Th className="text-right">Rechn.</SimpleTable.Th>
                  </SimpleTable.Tr>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {(industries.data ?? []).map((b, i) => (
                    <SimpleTable.Tr key={`${b.industryName}-${i}`}>
                      <SimpleTable.Td>{b.industryName}</SimpleTable.Td>
                      <SimpleTable.Td className="text-right font-medium">{EUR.format(b.totalRevenue)}</SimpleTable.Td>
                      <SimpleTable.Td className="text-right">{b.customerCount}</SimpleTable.Td>
                      <SimpleTable.Td className="text-right">{b.invoiceCount}</SimpleTable.Td>
                    </SimpleTable.Tr>
                  ))}
                </SimpleTable.Body>
              </SimpleTable>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Geo-Tab ────────────────────────────────────────────────────────────────

function GeographyTab({ geoTab, setGeoTab }: { geoTab: 'country' | 'state' | 'zip'; setGeoTab: (v: 'country' | 'state' | 'zip') => void }) {
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const q = useQuery<GeoResponse>({
    queryKey: ['customer-analytics', 'geography', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/geography').then((r) => r.data),
  })

  if (q.isLoading) return <LoadingState label="Lade Geografie…" />
  if (q.isError) return <ErrorState title="Geografie konnte nicht geladen werden" details={extractErrorDetails(q.error)} />
  if (!q.data) return null

  const data = geoTab === 'country' ? q.data.byCountry : geoTab === 'state' ? q.data.byState : q.data.byZipPrefix
  const total = data.reduce((s, b) => s + b.count, 0)
  const headerLabel = geoTab === 'zip' ? 'PLZ-Bereich' : geoTab === 'state' ? 'Bundesland' : 'Land'
  const t = q.data.totals
  const onlyUnknown = data.length === 1 && data[0]?.key === 'Unbekannt'

  return (
    <Card>
      <Card.Header>
        <Card.Title>Geografische Verteilung</Card.Title>
        <div className="flex gap-1">
          <Button variant={geoTab === 'country' ? 'primary' : 'ghost'} size="sm" onClick={() => setGeoTab('country')}>Land</Button>
          <Button variant={geoTab === 'state' ? 'primary' : 'ghost'} size="sm" onClick={() => setGeoTab('state')}>Bundesland</Button>
          <Button variant={geoTab === 'zip' ? 'primary' : 'ghost'} size="sm" onClick={() => setGeoTab('zip')}>PLZ-2</Button>
        </div>
      </Card.Header>
      <Card.Body>
        {t && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted">
            <span>{t.total} Kunden</span>
            <span>·</span>
            <span>{t.withCountry} mit Land ({t.total > 0 ? Math.round((t.withCountry / t.total) * 100) : 0}%)</span>
            <span>·</span>
            <span>{t.withZip} mit PLZ ({t.total > 0 ? Math.round((t.withZip / t.total) * 100) : 0}%)</span>
            <details className="ml-auto">
              <summary className="cursor-pointer">Quellen</summary>
              <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
                <span>address-JSON:</span><span>{t.sourceStored}</span>
                <span>sap_raw_json:</span><span>{t.sourceRaw}</span>
                <span>kombiniert:</span><span>{t.sourceMerged}</span>
                <span>keine Adresse:</span><span>{t.sourceNone}</span>
              </div>
            </details>
          </div>
        )}
        {data.length === 0 || onlyUnknown ? (
          <div className="space-y-3">
            <EmptyState
              title="Keine Adressdaten"
              description={
                onlyUnknown
                  ? 'Für die gewählten Kunden liegen weder im address-JSON noch im sap_raw_json befüllte Adressfelder vor. Nächster SAP-Stammdaten-Sync sollte das beheben.'
                  : 'Keine Adressen in dieser Dimension vorhanden.'
              }
            />
            {q.data.rawSample && (
              <details className="text-xs text-muted border border-border rounded p-3">
                <summary className="cursor-pointer font-medium">Technische Details (SAP-Antwort-Sample)</summary>
                <div className="mt-2 space-y-1">
                  <div>Sample-CardCode: <span className="font-mono">{q.data.rawSample.cardCode ?? '—'}</span></div>
                  <div>Adress-Felder in sap_raw_json: {q.data.rawSample.addressKeys.length > 0 ? (
                    <span className="font-mono">{q.data.rawSample.addressKeys.join(', ')}</span>
                  ) : (
                    <span className="italic">keine</span>
                  )}</div>
                  <div>BPAddresses-Property vorhanden: {q.data.rawSample.hasBPAddresses ? 'ja' : 'nein'}</div>
                  <div>BPAddresses-Einträge: {q.data.rawSample.bpAddressesLength}</div>
                  <div className="pt-2 text-muted/80">
                    Wenn „BPAddresses vorhanden: nein" steht, fehlt im SAP-Sync das <code>$expand=BPAddresses</code>.
                    Das wird mit diesem Deploy gesetzt — bitte einen SAP-Stammdaten-Sync auslösen
                    (Einkauf → Stammdaten-Abgleich), damit sap_raw_json neu befüllt wird.
                  </div>
                </div>
              </details>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={data.slice(0, 12)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <ReTooltip />
                  <Bar dataKey="count" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <BucketTable
              headers={[headerLabel, 'Anzahl', 'Anteil']}
              rows={data.map((b) => ({
                key: b.key,
                cells: [b.label, String(b.count), total > 0 ? `${((b.count / total) * 100).toFixed(1)}%` : '—'],
                alignRight: [false, true, true],
              }))}
            />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Aktivitaet-Tab ──────────────────────────────────────────────────────────

function ActivityTab() {
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const q = useQuery<ActivityResponse>({
    queryKey: ['customer-analytics', 'activity', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/activity').then((r) => r.data),
  })
  if (q.isLoading) return <LoadingState label="Lade Aktivitätsdaten…" />
  if (q.isError) return <ErrorState title="Aktivität konnte nicht geladen werden" details={extractErrorDetails(q.error)} />
  if (!q.data) return null

  const statusData = [
    { name: 'Aktiv', value: q.data.active },
    { name: 'Inaktiv', value: q.data.inactive },
  ]

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card>
        <Card.Header>
          <Card.Title>Aktiv vs. Inaktiv</Card.Title>
          {q.data.activityDefinition ? (
            <span className="text-caption text-gray-500 ml-2">{q.data.activityDefinition}</span>
          ) : null}
        </Card.Header>
        <Card.Body>
          <div className="h-64">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label>
                  <Cell fill="#16a34a" />
                  <Cell fill="#9ca3af" />
                </Pie>
                <ReTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 text-small mt-2">
            <span><span className="inline-block w-3 h-3 bg-green-600 rounded-sm mr-1" />Aktiv: {q.data.active}</span>
            <span><span className="inline-block w-3 h-3 bg-gray-400 rounded-sm mr-1" />Inaktiv: {q.data.inactive}</span>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><Card.Title>Neukunden je Quartal (12 Quartale)</Card.Title></Card.Header>
        <Card.Body>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={q.data.newPerQuarter}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <ReTooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Enrichment-Run-Typen ────────────────────────────────────────────────────

interface EnrichmentBatchResult {
  processed: number
  remaining: number
  results: Array<{
    customerId: string
    customerName: string
    status: 'skipped' | 'auto_applied' | 'pending' | 'error'
    error?: string
  }>
}

// ─── Suggestions / Review-Queue ─────────────────────────────────────────────

function SuggestionsTab({ catalogIsEmpty }: { catalogIsEmpty?: boolean }) {
  const queryClient = useQueryClient()
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const [overrideOpen, setOverrideOpen] = useState<Suggestion | null>(null)
  const [overrideCode, setOverrideCode] = useState<string>('')
  const [enrichLog, setEnrichLog] = useState<EnrichmentBatchResult | null>(null)
  const abortRef = useRef(false)
  const autoRef = useRef(false)

  const suggestions = useQuery<Suggestion[]>({
    queryKey: ['customer-analytics', 'suggestions', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/industry-suggestions?limit=200').then((r) => r.data),
  })

  const enrichmentStatus = useQuery<EnrichmentStatus>({
    queryKey: ['customer-analytics', 'enrichment-status', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/enrichment-status').then((r) => r.data),
    enabled: !catalogIsEmpty,
  })

  // Catalog-Query lebt auf Page-Ebene (catalogIsEmpty-Prop), hier nur fuer das Override-Modal.
  const catalog = useQuery<CatalogEntry[]>({
    queryKey: ['customer-analytics', 'industry-catalog', activeTenantIds],
    queryFn: () => api.get('/customer-analytics/industry-catalog').then((r) => r.data),
  })

  const apply = useMutation({
    mutationFn: async (vars: { id: string; action: 'accept' | 'reject' | 'override'; sapCode?: string; industryName?: string }) => {
      const { id, ...body } = vars
      const r = await api.post(`/customer-analytics/industry-suggestions/${id}/apply`, body)
      return r.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-analytics'] })
    },
  })

  const bulkApply = useMutation({
    mutationFn: async (vars: { action: 'accept' | 'reject'; minConfidence?: number }) => {
      const r = await api.post('/customer-analytics/industry-suggestions/bulk-apply', vars)
      return r.data as { processed: number; succeeded: number; failed: number; errors: string[] }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customer-analytics'] })
    },
  })

  const triggerEnrichment = useMutation({
    mutationFn: async (limit: number): Promise<EnrichmentBatchResult> => {
      const r = await api.post('/customer-analytics/trigger-enrichment', { limit })
      return r.data
    },
    onSuccess: (data) => {
      setEnrichLog(data)
      void queryClient.invalidateQueries({ queryKey: ['customer-analytics'] })
      if (autoRef.current && data.remaining > 0) {
        triggerEnrichment.mutate(50)
      } else {
        autoRef.current = false
      }
    },
  })

  const catalogByCode = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of catalog.data ?? []) m.set(c.sapCode, c.name)
    return m
  }, [catalog.data])

  if (suggestions.isLoading) return <LoadingState label="Lade Vorschläge…" />
  if (suggestions.isError) return <ErrorState title="Vorschläge konnten nicht geladen werden" details={extractErrorDetails(suggestions.error)} />

  const list = suggestions.data ?? []
  const status = enrichmentStatus.data

  return (
    <Card>
      <Card.Header>
        <Card.Title>KI-Vorschläge zur Branche</Card.Title>
        <span className="text-small text-gray-500">{list.length} offen</span>
      </Card.Header>
      <Card.Body>
        {catalogIsEmpty ? (
          <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-small text-amber-800">
            <div className="font-medium">Branchen-Katalog ist leer — keine KI-Analyse möglich</div>
            <div className="mt-1">
              Die <code>industries</code>-Tabelle ist leer. Solange kein Katalog existiert, gibt die
              KI-Erkennung bei jedem Sync sofort <em>skipped</em> zurück und es entstehen keine
              Vorschläge. Klicke oben rechts auf <strong>„Branchen-Katalog generieren"</strong>, wähle
              passende NACE-Branchen und übernimm sie in SAP — danach startet die automatische Analyse.
            </div>
          </div>
        ) : null}

        {/* Diagnostik-Box + On-Demand-Trigger: nur anzeigen wenn Katalog vorhanden */}
        {!catalogIsEmpty && status ? (
          <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-small text-blue-900 space-y-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="font-medium text-blue-800">Status der KI-Branchenerkennung</div>
              {status.customersWithoutIndustry > 0 ? (
                <div className="flex items-center gap-2">
                  {triggerEnrichment.isPending && autoRef.current ? (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => { autoRef.current = false }}
                    >
                      Stopp
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={triggerEnrichment.isPending}
                      onClick={() => {
                        autoRef.current = true
                        abortRef.current = false
                        setEnrichLog(null)
                        triggerEnrichment.mutate(50)
                      }}
                    >
                      <Play className="w-3 h-3" />
                      {triggerEnrichment.isPending
                        ? 'KI läuft…'
                        : `Alle ${status.customersWithoutIndustry} klassifizieren`}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={triggerEnrichment.isPending}
                    onClick={() => {
                      autoRef.current = false
                      abortRef.current = false
                      setEnrichLog(null)
                      triggerEnrichment.mutate(50)
                    }}
                  >
                    <Play className="w-3 h-3" />
                    {triggerEnrichment.isPending && !autoRef.current ? 'Läuft…' : '50 klassifizieren'}
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
              <div>
                <span className="font-medium">{status.customersWithoutIndustry}</span>
                <span className="text-blue-700"> Kunden noch ohne Branche</span>
              </div>
              <div>
                <span className="font-medium">{status.pendingCount}</span>
                <span className="text-blue-700"> Vorschläge offen (manuell prüfen)</span>
              </div>
              <div>
                <span className="font-medium">{status.autoAppliedCount}</span>
                <span className="text-blue-700"> automatisch übernommen</span>
              </div>
              {status.customersWithLocalIndustry > 0 ? (
                <div className="col-span-2 sm:col-span-3 text-amber-700 font-medium">
                  {status.customersWithLocalIndustry} Kunden mit LOCAL:-Code — SAP-Schreiben nicht möglich, Branche nur lokal gesetzt.
                </div>
              ) : null}
            </div>

            {/* Lauf-Ergebnis des On-Demand-Triggers */}
            {triggerEnrichment.isError ? (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
                KI-Lauf fehlgeschlagen: {String((triggerEnrichment.error as Error)?.message ?? triggerEnrichment.error)}
              </div>
            ) : null}
            {enrichLog ? (
              <div className="rounded-md bg-white border border-blue-200 px-3 py-2 space-y-1">
                <div className="font-medium text-blue-900">
                  Lauf abgeschlossen: {enrichLog.processed} verarbeitet
                  {enrichLog.remaining > 0 ? `, noch ${enrichLog.remaining} ausstehend` : ' — alle fertig'}
                </div>
                <div className="text-blue-700 text-caption space-y-0.5">
                  {enrichLog.results.map((r, i) => (
                    <div key={`${r.customerId}-${i}`}>
                      <span className="font-medium">{r.customerName}:</span>{' '}
                      {r.status === 'auto_applied' ? '✓ automatisch übernommen'
                        : r.status === 'pending' ? '→ in Review-Queue'
                        : r.status === 'skipped' ? '— übersprungen'
                        : `✗ Fehler: ${r.error ?? '—'}`}
                    </div>
                  ))}
                </div>
                {enrichLog.remaining > 0 ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={triggerEnrichment.isPending}
                      onClick={() => {
                        autoRef.current = false
                        triggerEnrichment.mutate(50)
                      }}
                    >
                      {triggerEnrichment.isPending ? 'Läuft…' : `Nächste 50 von ${enrichLog.remaining}`}
                    </Button>
                    {!autoRef.current ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={triggerEnrichment.isPending}
                        onClick={() => {
                          autoRef.current = true
                          triggerEnrichment.mutate(50)
                        }}
                      >
                        Alle {enrichLog.remaining} klassifizieren
                      </Button>
                    ) : (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => { autoRef.current = false }}
                      >
                        Stopp
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ) : null}

            {!enrichLog && !triggerEnrichment.isPending && status.pendingCount === 0 && status.customersWithoutIndustry > 0 ? (
              <div className="text-blue-700">
                ↳ Klicke „Jetzt klassifizieren" — kein Warten auf den Sync nötig.
              </div>
            ) : status.pendingCount > 0 ? (
              <div className="text-blue-700">
                ↳ {status.pendingCount} Vorschläge warten auf deine Entscheidung. Die KI-Konfidenz lag unter dem Auto-Apply-Schwellwert — nutze „Alle übernehmen".
              </div>
            ) : status.customersWithoutIndustry === 0 ? (
              <div className="text-green-700">
                ↳ Alle Kunden haben eine Branche.
              </div>
            ) : null}
          </div>
        ) : null}

        {list.length === 0 ? (
          <EmptyState
            title="Keine offenen Vorschläge"
            description={catalogIsEmpty
              ? 'Branchen-Katalog ist leer — erst nach dem Anlegen des Katalogs werden Vorschläge generiert.'
              : 'Beim nächsten SAP-Sync werden Kunden ohne Branche automatisch analysiert. Bei hoher KI-Konfidenz wird der Wert direkt nach SAP geschrieben, sonst landet er hier.'}
          />
        ) : (
          <div className="space-y-2">
            {apply.isError ? (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
                Aktion fehlgeschlagen: {String((apply.error as Error)?.message ?? apply.error)}
              </div>
            ) : null}
            {bulkApply.isError ? (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
                Bulk-Aktion fehlgeschlagen: {String((bulkApply.error as Error)?.message ?? bulkApply.error)}
              </div>
            ) : null}
            {bulkApply.data && (
              <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-small text-green-800">
                {bulkApply.data.succeeded} von {bulkApply.data.processed} Vorschlägen übernommen
                {bulkApply.data.failed > 0 ? ` · ${bulkApply.data.failed} Fehler` : ''}
              </div>
            )}

            {/* Bulk-Accept/Reject Toolbar */}
            <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-gray-100">
              <Button
                size="sm"
                variant="primary"
                disabled={bulkApply.isPending || apply.isPending}
                onClick={() => bulkApply.mutate({ action: 'accept' })}
              >
                Alle übernehmen ({list.length})
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkApply.isPending || apply.isPending}
                onClick={() => bulkApply.mutate({ action: 'accept', minConfidence: 0.7 })}
              >
                Alle mit Konfidenz ≥ 70% übernehmen
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={bulkApply.isPending || apply.isPending}
                onClick={() => {
                  if (window.confirm(`Alle ${list.length} Vorschläge verwerfen? Die KI kann beim nächsten SAP-Sync neue Vorschläge generieren.`)) {
                    bulkApply.mutate({ action: 'reject' })
                  }
                }}
              >
                Alle verwerfen
              </Button>
            </div>

            <SimpleTable>
              <SimpleTable.Head>
                <SimpleTable.Tr>
                  <SimpleTable.Th>Kunde</SimpleTable.Th>
                  <SimpleTable.Th>KI-Vorschlag</SimpleTable.Th>
                  <SimpleTable.Th className="text-right">Konfidenz</SimpleTable.Th>
                  <SimpleTable.Th>Begründung</SimpleTable.Th>
                  <SimpleTable.Th className="text-right">Aktion</SimpleTable.Th>
                </SimpleTable.Tr>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {list.map((s) => (
                  <SimpleTable.Tr key={s.id}>
                    <SimpleTable.Td>{s.customerName}</SimpleTable.Td>
                    <SimpleTable.Td>
                      <div className="font-medium">{s.suggestedName}</div>
                      <div className="text-caption text-gray-500">
                        code: {s.suggestedSapCode}
                        {s.suggestedSapCode.startsWith('LOCAL:') && (
                          <Badge variant="warning" size="sm" className="ml-1">LOCAL</Badge>
                        )}
                      </div>
                    </SimpleTable.Td>
                    <SimpleTable.Td className="text-right">
                      {s.confidence != null ? (
                        <Badge variant={s.confidence >= 0.7 ? 'success' : s.confidence >= 0.5 ? 'warning' : 'neutral'}>
                          {(s.confidence * 100).toFixed(0)}%
                        </Badge>
                      ) : '—'}
                    </SimpleTable.Td>
                    <SimpleTable.Td>
                      <span className="text-small text-gray-600">{s.reasoning ?? '—'}</span>
                    </SimpleTable.Td>
                    <SimpleTable.Td className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="xs"
                          variant="primary"
                          disabled={apply.isPending || bulkApply.isPending}
                          onClick={() => apply.mutate({ id: s.id, action: 'accept' })}
                        >
                          Übernehmen
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={apply.isPending || bulkApply.isPending}
                          onClick={() => { setOverrideOpen(s); setOverrideCode('') }}
                        >
                          Andere…
                        </Button>
                        <Button
                          size="xs"
                          variant="danger"
                          disabled={apply.isPending || bulkApply.isPending}
                          onClick={() => apply.mutate({ id: s.id, action: 'reject' })}
                        >
                          Verwerfen
                        </Button>
                      </div>
                    </SimpleTable.Td>
                  </SimpleTable.Tr>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
          </div>
        )}

        <Modal
          open={!!overrideOpen}
          onOpenChange={(o) => { if (!o) setOverrideOpen(null) }}
          title="Branche überschreiben"
        >
          {overrideOpen ? (
            <div className="space-y-3">
              <p className="text-small text-gray-600">
                Wähle die korrekte Branche für <strong>{overrideOpen.customerName}</strong>.
                Der Wert wird in SAP zurückgeschrieben.
              </p>
              <Select
                value={overrideCode}
                onValueChange={setOverrideCode}
                placeholder="Branche wählen…"
                options={(catalog.data ?? []).map((c) => ({ value: c.sapCode, label: c.name }))}
              />
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setOverrideOpen(null)}>Abbrechen</Button>
                <Button
                  variant="primary"
                  disabled={!overrideCode || apply.isPending}
                  onClick={() => {
                    if (!overrideOpen || !overrideCode) return
                    apply.mutate({
                      id: overrideOpen.id,
                      action: 'override',
                      sapCode: overrideCode,
                      industryName: catalogByCode.get(overrideCode),
                    })
                    setOverrideOpen(null)
                  }}
                >
                  Speichern
                </Button>
              </div>
            </div>
          ) : null}
        </Modal>
      </Card.Body>
    </Card>
  )
}

// ─── BucketTable Helper ────────────────────────────────────────────────────

function BucketTable({ headers, rows }: {
  headers: string[]
  rows: Array<{ key: string; cells: string[]; alignRight: boolean[] }>
}) {
  return (
    <SimpleTable>
      <SimpleTable.Head>
        <SimpleTable.Tr>
          {headers.map((h, i) => (
            <SimpleTable.Th key={i} className={rows[0]?.alignRight[i] ? 'text-right' : ''}>{h}</SimpleTable.Th>
          ))}
        </SimpleTable.Tr>
      </SimpleTable.Head>
      <SimpleTable.Body>
        {rows.map((r) => (
          <SimpleTable.Tr key={r.key}>
            {r.cells.map((c, i) => (
              <SimpleTable.Td key={i} className={r.alignRight[i] ? 'text-right' : ''}>{c}</SimpleTable.Td>
            ))}
          </SimpleTable.Tr>
        ))}
      </SimpleTable.Body>
    </SimpleTable>
  )
}

// ─── Branchen-Katalog-Generator-Modal ───────────────────────────────────────

interface NaceItem {
  code: string
  section: string
  name: string
}

interface GeneratorResponse {
  ok: boolean
  catalog: NaceItem[]
  customerSampleSize: number
  modelUsed?: string
  error?: string
}

interface EntityProperty {
  name: string
  type: string
  nullable: boolean
  maxLength?: number
  isKey?: boolean
}
interface EntitySchemaResponse {
  entityName: string
  found: boolean
  properties: EntityProperty[]
  metadataBytes?: number
}

interface CommitResponse {
  ok: boolean
  createdInSap: number
  createdLocallyOnly?: number
  sapNotWriteable?: boolean
  alreadyExisted: number
  errors: string[]
}

function CatalogGeneratorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<NaceItem[]>([])
  const [info, setInfo] = useState<string>('')
  const [draftCode, setDraftCode] = useState('')
  const [draftName, setDraftName] = useState('')

  const generate = useMutation({
    mutationFn: async (): Promise<GeneratorResponse> =>
      api.post('/customer-analytics/generate-catalog').then((r) => r.data),
    onSuccess: (data) => {
      if (data.ok) {
        setItems(data.catalog)
        setInfo(`KI hat ${data.catalog.length} NACE-Abteilungen aus ${data.customerSampleSize} Kunden ausgewählt${data.modelUsed ? ` (${data.modelUsed})` : ''}.`)
      } else {
        setInfo(data.error ?? 'KI-Vorschlag fehlgeschlagen.')
      }
    },
  })

  const loadFull = useMutation({
    mutationFn: async (): Promise<NaceItem[]> =>
      api.get('/customer-analytics/nace-catalog').then((r) => r.data),
    onSuccess: (data) => {
      setItems(data)
      setInfo(`Kompletter NACE-Rev.-2-Katalog geladen (${data.length} Abteilungen). Reviewe und entferne, was nicht zur Kundenstruktur passt.`)
    },
  })

  const [industrySchema, setIndustrySchema] = useState<EntitySchemaResponse | null>(null)
  const inspectSchema = useMutation({
    mutationFn: async (): Promise<EntitySchemaResponse> =>
      api.get('/admin/sap/inspect-entity?name=Industry').then((r) => r.data),
    onSuccess: (data) => setIndustrySchema(data),
  })

  const [diagResult, setDiagResult] = useState<unknown>(null)
  const testCreate = useMutation({
    mutationFn: async (): Promise<unknown> =>
      api.post('/admin/sap/test-create-industry', { code: 99, name: 'TEST 99 – SysMan Diagnose', prop: 'IndustryName' }).then((r) => r.data),
    onSuccess: (data) => setDiagResult(data),
  })

  const commit = useMutation({
    mutationFn: async (its: NaceItem[]): Promise<CommitResponse> =>
      api.post('/customer-analytics/commit-catalog-to-sap', { items: its }).then((r) => r.data),
    onSuccess: (data) => {
      const parts = [`${data.createdInSap} in SAP angelegt`]
      if ((data.createdLocallyOnly ?? 0) > 0) parts.push(`${data.createdLocallyOnly} nur lokal angelegt`)
      parts.push(`${data.alreadyExisted} bereits vorhanden`)
      if (data.sapNotWriteable) {
        parts.push('\n⚠ SAP /Industries ist in dieser Instanz read-only. Die Branchen wurden lokal angelegt — Auswertungen funktionieren, aber `BusinessPartner.Industry` in SAP bleibt unverändert. Wende dich an euren SAP-Admin, falls Industries in SAP gepflegt werden sollen.')
        // Automatisch das SAP-Industry-Schema laden, damit der User die echten
        // Property-Namen sieht und ggf. die Fallback-Liste in createIndustry
        // erweitern kann.
        if (!industrySchema && !inspectSchema.isPending) inspectSchema.mutate()
      }
      if (data.errors.length > 0) parts.push(`${data.errors.length} Fehler`)
      setInfo(parts.join(' · ') + (data.errors.length > 0 ? '\n' + data.errors.slice(0, 5).join('\n') : ''))
      void queryClient.invalidateQueries({ queryKey: ['customer-analytics'] })
    },
  })

  const handleClose = () => {
    if (commit.isPending || generate.isPending || loadFull.isPending) return
    setItems([])
    setInfo('')
    setDraftCode('')
    setDraftName('')
    onClose()
  }

  const removeItem = (idx: number) => setItems((arr) => arr.filter((_, i) => i !== idx))
  const addItem = () => {
    const code = draftCode.trim().padStart(2, '0')
    const name = draftName.trim()
    if (!code || !name) return
    if (items.some((i) => i.code === code)) return
    setItems((arr) => [...arr, { code, section: '?', name }].sort((a, b) => a.code.localeCompare(b.code)))
    setDraftCode('')
    setDraftName('')
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) handleClose() }}
      title="Branchen-Katalog generieren (NACE Rev. 2 / WZ 2008)"
      size="lg"
    >
      <div className="space-y-3">
        <p className="text-small text-gray-600">
          Die KI analysiert die Namen aller Kunden im aktiven Mandanten und wählt aus dem
          NACE-Rev.-2-Katalog (EU-Standard, in Deutschland WZ&nbsp;2008) die passenden Abteilungen.
          Reviewe die Liste, entferne unpassende Codes oder ergänze fehlende, und schicke sie
          dann nach SAP (POST&nbsp;/Industries als "<em>Code — Bezeichnung</em>").
        </p>

        {items.length === 0 ? (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              disabled={generate.isPending || loadFull.isPending}
              onClick={() => generate.mutate()}
            >
              <Sparkles className="w-4 h-4" /> {generate.isPending ? 'Generiere…' : 'KI-Vorschlag generieren'}
            </Button>
            <Button
              variant="ghost"
              disabled={generate.isPending || loadFull.isPending}
              onClick={() => loadFull.mutate()}
            >
              {loadFull.isPending ? 'Lade…' : 'Vollständigen NACE-Katalog laden'}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-small font-medium text-gray-700">{items.length} NACE-Abteilungen</span>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={generate.isPending || commit.isPending || loadFull.isPending}
                  onClick={() => generate.mutate()}
                >
                  Neu von KI
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={generate.isPending || commit.isPending || loadFull.isPending}
                  onClick={() => loadFull.mutate()}
                >
                  Komplett laden
                </Button>
              </div>
            </div>
            <div className="border border-gray-200 rounded-md p-2 max-h-72 overflow-y-auto space-y-1">
              {items.map((it, i) => (
                <div key={`${it.code}-${i}`} className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-gray-50 rounded">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-small text-gray-500 w-6">{it.code}</span>
                    <span className="text-body">{it.name}</span>
                    <span className="text-caption text-gray-400">{it.section}</span>
                  </div>
                  <Button variant="ghost" size="xs" onClick={() => removeItem(i)}>Entfernen</Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="w-20 border border-gray-300 rounded-md px-2 py-1 text-body font-mono"
                placeholder="Code"
                value={draftCode}
                onChange={(e) => setDraftCode(e.target.value)}
                maxLength={2}
              />
              <input
                type="text"
                className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-body"
                placeholder="Eigene NACE-Bezeichnung hinzufügen…"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem() } }}
              />
              <Button variant="ghost" size="sm" onClick={addItem} disabled={!draftCode.trim() || !draftName.trim()}>Hinzufügen</Button>
            </div>
          </div>
        )}

        {info ? (
          <div className="text-small text-gray-700 whitespace-pre-line bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
            {info}
          </div>
        ) : null}

        {industrySchema ? (
          <div className="text-small bg-blue-50 border border-blue-200 rounded-md px-3 py-2 space-y-1">
            <div className="font-medium text-blue-900">
              SAP-Schema „{industrySchema.entityName}" {industrySchema.found ? `— ${industrySchema.properties.length} Properties` : '— nicht gefunden'}
            </div>
            {industrySchema.found ? (
              <table className="text-caption w-full">
                <thead>
                  <tr className="text-gray-600">
                    <th className="text-left">Property</th>
                    <th className="text-left">Type</th>
                    <th className="text-left">Nullable</th>
                    <th className="text-left">MaxLen</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {industrySchema.properties.map((p) => (
                    <tr key={p.name} className="text-gray-800">
                      <td>{p.name}{p.isKey ? ' 🔑' : ''}</td>
                      <td>{p.type}</td>
                      <td>{p.nullable ? 'yes' : 'no'}</td>
                      <td>{p.maxLength ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-gray-700">
                EntityType im SAP-Metadata-XML nicht gefunden. Vermutlich heißt sie anders (z.B. „IndustriesService" oder „Industry"). Frag den SAP-Admin nach dem genauen Namen.
              </div>
            )}
          </div>
        ) : null}

        {generate.isError ? (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
            KI-Vorschlag fehlgeschlagen: {String((generate.error as Error)?.message ?? generate.error)}
          </div>
        ) : null}
        {commit.isError ? (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
            SAP-Schreiben fehlgeschlagen: {String((commit.error as Error)?.message ?? commit.error)}
          </div>
        ) : null}

        {diagResult ? (
          <div className="text-small bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <div className="font-medium text-amber-900 mb-1">SAP-Diagnose: POST /Industries mit Code 99 + IndustryName</div>
            <pre className="text-caption font-mono whitespace-pre-wrap break-words">{JSON.stringify(diagResult, null, 2)}</pre>
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button
            variant="ghost"
            size="sm"
            disabled={inspectSchema.isPending}
            onClick={() => inspectSchema.mutate()}
            title="Liest /b1s/v1/$metadata aus eurer SAP und zeigt die echten Property-Namen der Industry-Entity"
          >
            {inspectSchema.isPending ? 'Lese Schema…' : 'SAP-Schema prüfen'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={testCreate.isPending}
            onClick={() => testCreate.mutate()}
            title="Sendet einen einzelnen Test-POST mit Code=99 + IndustryName, zeigt die rohe SAP-Antwort"
          >
            {testCreate.isPending ? 'Teste POST…' : 'POST testen'}
          </Button>
          <Button variant="ghost" onClick={handleClose} disabled={commit.isPending}>Schließen</Button>
          <Button
            variant="primary"
            disabled={items.length === 0 || commit.isPending}
            onClick={() => commit.mutate(items)}
          >
            {commit.isPending ? 'Schreibe nach SAP…' : `${items.length} Branchen in SAP übernehmen`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
